import React, { useState, useEffect } from 'react';
import { api } from '../api';
import CreateCampaignModal from '../components/CreateCampaignModal';
import LeadsModal from '../components/LeadsModal';

function Campaigns({ user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const isAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const data = await api.getCampaigns();
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async (id) => {
    try {
      await api.startCampaign(id);
      loadCampaigns();
    } catch (error) {
      alert(error.message);
    }
  };

  const handlePause = async (id) => {
    try {
      await api.pauseCampaign(id);
      loadCampaigns();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleStop = async (id) => {
    try {
      await api.stopCampaign(id);
      loadCampaigns();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.deleteCampaign(id);
      loadCampaigns();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUploadLeads = (campaign) => {
    setSelectedCampaign(campaign);
    setShowLeadsModal(true);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 600 }}>Campaigns</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
          data-testid="btn-create-campaign"
        >
          + Create Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
            No campaigns yet. Create your first campaign to get started!
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table data-testid="campaigns-table">
              <thead>
                <tr>
                  {isAdmin && <th>Tenant</th>}
                  <th>Campaign Name</th>
                  <th>Status</th>
                  <th>Leads</th>
                  <th>Dialed</th>
                  <th>Transferred</th>
                  <th>Audio</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(campaign => (
                  <tr key={campaign.id}>
                    {isAdmin && (
                      <td style={{ fontWeight: 500, color: '#5dade2' }}>
                        {campaign.tenant_name || campaign.company_name || 'Unknown'}
                      </td>
                    )}
                    <td>{campaign.name}</td>
                    <td>
                      <span className={`badge badge-${campaign.status}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td>{campaign.stats.total_leads}</td>
                    <td>{campaign.stats.dialed}</td>
                    <td>{campaign.stats.transferred}</td>
                    <td style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                      {campaign.audio_filename}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {campaign.status === 'draft' && (
                          <>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                              onClick={() => handleUploadLeads(campaign)}
                              data-testid={`btn-upload-leads-${campaign.id}`}
                            >
                              Upload Leads
                            </button>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                              onClick={() => handleStart(campaign.id)}
                              data-testid={`btn-start-${campaign.id}`}
                            >
                              Start
                            </button>
                          </>
                        )}
                        {campaign.status === 'active' && (
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                            onClick={() => handlePause(campaign.id)}
                            data-testid={`btn-pause-${campaign.id}`}
                          >
                            Pause
                          </button>
                        )}
                        {campaign.status === 'paused' && (
                          <>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                              onClick={() => handleStart(campaign.id)}
                            >
                              Resume
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                              onClick={() => handleStop(campaign.id)}
                            >
                              Stop
                            </button>
                          </>
                        )}
                        <button
                          className="btn btn-danger"
                          style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                          onClick={() => handleDelete(campaign.id)}
                          data-testid={`btn-delete-${campaign.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadCampaigns();
          }}
        />
      )}

      {showLeadsModal && selectedCampaign && (
        <LeadsModal
          campaign={selectedCampaign}
          onClose={() => {
            setShowLeadsModal(false);
            setSelectedCampaign(null);
          }}
          onSuccess={() => {
            setShowLeadsModal(false);
            setSelectedCampaign(null);
            loadCampaigns();
          }}
        />
      )}
    </div>
  );
}

export default Campaigns;
